<script lang="ts">
  // ActionModal: typed-confirm gate for a single mutate.
  //
  // PR 3 (auth-via-telegram-otp) — the per-action TOTP input was
  // removed. The mutate gate on the agent side dropped TOTP in
  // PR 1 (mutates now require ONLY a valid session cookie +
  // CSRF token), so the UI input was a cosmetic mismatch: the
  // entered code was sent in the payload, silently dropped by the
  // backend (`mutate.ts` ignores the legacy `totp` field), and
  // presented the operator with a friction prompt that did
  // nothing. This component now keeps ONLY the typed-confirm
  // gate, which is the real defence against mis-clicks /
  // accidental bulk selects.
  //
  // - Typed-confirm: user must type the target container's name
  //   (or "bulk" for the bulk action, or the absolute file path
  //   for the storage save flow) before the Confirm button
  //   enables.
  // - Focus trap: focus moves into the modal on open, Tab cycles
  //   inside the dialog, focus returns to the trigger on close.
  // - Esc cancels.
  // - No new external modal deps; uses native <dialog> only if
  //   the browser supports the showModal() API, with a
  //   div+role="dialog" fallback so the modal still renders
  //   during the SPA build's headless smoke tests.
  //
  // The parent owns the actual mutate call — this component
  // emits `confirm` with an empty payload ONLY after the
  // typed-confirm matches. The parent then POSTs to
  // /api/restart|/api/stop|/api/start with the CSRF token from
  // the cookie session in X-CSRF-Token (see lib/mutate.ts).

  import { onMount, tick, createEventDispatcher } from "svelte";
  import type { MutateAction } from "../lib/bindings";

  export let open = false;
  export let action: MutateAction = "restart";
  /** Container name to require typed-confirm against. For bulk
   * actions, the parent passes "bulk" (or any sentinel string).
   * PR 4 also accepts an absolute file path (storage save). */
  export let container: string = "";
  /** Alias for `container` — preferred for non-container flows
   * (e.g. storage save where the typed-confirm is an absolute
   * file path, not a container name). When set, takes precedence
   * over `container` for the typed-confirm target. */
  export let target: string = "";
  /** Optional list of selected containers, shown when `bulk`. */
  export let bulkTargets: string[] = [];
  /** Disable the Confirm button while the parent's mutate is in
   * flight (the parent sets true and then waits for the response). */
  export let busy = false;

  const dispatch = createEventDispatcher<{
    confirm: Record<string, never>;
    cancel: void;
  }>();

  let confirmText = "";
  let dialogEl: HTMLDialogElement | HTMLDivElement | undefined;
  let confirmInputEl: HTMLInputElement | undefined;
  let lastFocused: HTMLElement | null = null;

  $: confirmTarget = bulkTargets.length > 0 ? "bulk" : target || container;
  $: canSubmit = confirmText === confirmTarget && !busy;

  // Action verbs used in the modal title + button labels.
  const ACTION_VERB: Record<MutateAction, string> = {
    restart: "Restart",
    stop: "Stop",
    start: "Start",
  };

  async function handleOpenChanged() {
    if (open) {
      // Capture the previously-focused element so we can restore on
      // close (focus-trap return).
      lastFocused = document.activeElement as HTMLElement | null;
      // Reset state on every open.
      confirmText = "";
      await tick();
      // Move focus into the modal. If the native <dialog> API is
      // available use showModal(); otherwise just focus the input —
      // the dialog still renders, just without the backdrop layer.
      if (dialogEl && "showModal" in dialogEl) {
        try {
          (dialogEl as HTMLDialogElement).showModal();
        } catch {
          /* already open — ignore */
        }
      }
      confirmInputEl?.focus();
    } else {
      if (dialogEl && "close" in dialogEl) {
        try {
          (dialogEl as HTMLDialogElement).close();
        } catch {
          /* not open — ignore */
        }
      }
      lastFocused?.focus();
    }
  }

  $: open, handleOpenChanged();

  function onConfirmInput(e: Event) {
    confirmText = (e.target as HTMLInputElement).value;
  }

  function submit() {
    if (!canSubmit) return;
    // The dispatch carries no payload — the parent's mutate call
    // reads session cookie + CSRF header directly.
    dispatch("confirm", {});
  }

  function cancel() {
    dispatch("cancel");
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
      return;
    }
    if (e.key === "Tab") {
      // Minimal focus trap: keep Tab inside the dialog. Focusable
      // children are: the typed-confirm input (first), Cancel and
      // Confirm buttons (last).
      const focusable = dialogEl?.querySelectorAll<HTMLElement>(
        'input, button, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  onMount(() => {
    return () => {
      // Cleanup: if the component is destroyed while open, return
      // focus to the trigger to avoid stranding the focus on a
      // detached element.
      lastFocused?.focus();
    };
  });
</script>

{#if open}
  <svelte:element
    this={"dialog"}
    bind:this={dialogEl}
    class="action-modal"
    data-testid="action-modal"
    data-action={action}
    role="dialog"
    aria-modal="true"
    aria-labelledby="action-modal-title"
    tabindex="-1"
    on:keydown={onKeydown}
    on:click|self={cancel}
  >
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div
      class="action-modal__card"
      role="document"
      on:click|stopPropagation
    >
      <h2 id="action-modal-title" class="action-modal__title">
        {ACTION_VERB[action]}
        {#if bulkTargets.length > 0}
          {bulkTargets.length} container{bulkTargets.length === 1 ? "" : "s"}
        {:else}
          {target || container}
        {/if}
      </h2>

      {#if bulkTargets.length > 0}
        <ul class="action-modal__bulk-list" data-testid="bulk-targets">
          {#each bulkTargets as t (t)}
            <li>{t}</li>
          {/each}
        </ul>
      {/if}

      <slot name="preview" />

      <p class="action-modal__muted">
        Type <code>{confirmTarget}</code> to confirm. (PR 3: the
        previous per-action TOTP code field was removed when the
        agent's mutate gate dropped TOTP — only the session cookie
        + CSRF token are required now.)
      </p>

      <label class="action-modal__field">
        <span>Type <code>{confirmTarget}</code> to confirm</span>
        <input
          bind:this={confirmInputEl}
          type="text"
          value={confirmText}
          on:input={onConfirmInput}
          placeholder={confirmTarget}
          data-testid="confirm-input"
          aria-label="Typed confirmation for {confirmTarget}"
        />
      </label>

      <div class="action-modal__actions">
        <button
          type="button"
          class="action-modal__btn action-modal__btn--ghost"
          on:click={cancel}
          data-testid="modal-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          class="action-modal__btn action-modal__btn--accent"
          on:click={submit}
          disabled={!canSubmit}
          data-testid="modal-confirm"
        >
          {busy ? "Working…" : ACTION_VERB[action]}
        </button>
      </div>
    </div>
  </svelte:element>
{/if}

<style>
  /* ActionModal — token-driven recolor of the PR 1 legacy modal.
   * Visual reference: openspec/changes/ui-redisign-v2/Untitled.fig
   * nodes 1:412-460 (modal frame + TOTP cells + typed-confirm input).
   * Per design ADR-3 the rules will eventually move to
   * styles/components.css; the inline <style> is kept for now so the
   * component stays self-contained. */
  :global(dialog.action-modal) {
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    background: var(--surface);
    color: var(--text-strong);
    padding: 0;
    max-width: 420px;
    width: 100%;
  }
  :global(dialog.action-modal::backdrop) {
    background: rgba(0, 0, 0, 0.65);
  }

  .action-modal__card {
    padding: var(--space-5) var(--space-6);
    display: grid;
    gap: var(--space-3);
  }

  .action-modal__title {
    margin: 0;
    font: var(--type-h1);
    color: var(--text-strong);
  }

  .action-modal__muted {
    color: var(--text-mid);
    font: var(--type-body-sm);
    margin: 0;
  }

  .action-modal__bulk-list {
    margin: 0;
    padding-left: var(--space-4);
    color: var(--text-mid);
    font: var(--type-mono-md);
    max-height: 6rem;
    overflow: auto;
  }

  .action-modal__field {
    display: grid;
    gap: var(--space-2);
    font: var(--type-body-sm);
  }
  .action-modal__field span {
    color: var(--text-mid);
  }
  .action-modal__field input {
    background: var(--surface-2);
    color: var(--text-strong);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: var(--space-3);
    font: var(--type-body);
    font-variant-numeric: tabular-nums;
  }
  .action-modal__field input:focus-visible {
    outline: 2px solid var(--accent-cyan);
    outline-offset: 1px;
    border-color: var(--accent-cyan);
  }

  .action-modal__actions {
    display: flex;
    gap: var(--space-3);
    justify-content: flex-end;
    margin-top: var(--space-2);
  }

  .action-modal__btn {
    height: 36px;
    padding: 0 var(--space-5);
    border-radius: var(--radius-sm);
    background: var(--surface-2);
    color: var(--text-strong);
    border: 1px solid var(--border);
    cursor: pointer;
    font: var(--type-body);
    font-weight: 500;
    transition:
      background var(--motion-fast) var(--easing-standard),
      color var(--motion-fast) var(--easing-standard);
  }
  .action-modal__btn--ghost {
    background: transparent;
    color: var(--text-mid);
  }
  .action-modal__btn--ghost:hover:not(:disabled) {
    background: var(--surface-2);
    color: var(--text-strong);
  }
  .action-modal__btn--accent {
    background: var(--accent-cyan);
    border-color: var(--accent-cyan);
    color: var(--bg);
  }
  .action-modal__btn--accent:hover:not(:disabled) {
    background: #00C7B2;
    border-color: #00C7B2;
  }
  .action-modal__btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  code {
    background: var(--surface-2);
    padding: 1px var(--space-2);
    border-radius: 4px;
    font: var(--type-mono-md);
  }
</style>